import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  signInWithPopup,
  OAuthProvider,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { User } from '../types';
import { generateFallbackUsername, setUsername as setUsernameService } from '../services/usernameService';

interface AuthContextType {
  user: FirebaseUser | null;
  userProfile: User | null;
  isLoading: boolean;
  needsUsername: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signOut: () => Promise<void>;
  updateUserProfile: (updates: Partial<User>) => void;
  setUserUsername: (username: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsUsername, setNeedsUsername] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        // Fetch or create user profile
        const userRef = doc(db, 'users', firebaseUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = { id: userSnap.id, ...userSnap.data() } as User;
          setUserProfile(userData);
          // Check if user needs to set username
          setNeedsUsername(!userData.username);
        } else {
          // Create new user profile with fallback username
          const email = firebaseUser.email || '';
          const fallbackUsername = generateFallbackUsername(email);

          const newUser = {
            name: firebaseUser.displayName || 'Anonymous',
            email: email,
            image: firebaseUser.photoURL || undefined,
            username: fallbackUsername,
            createdAt: serverTimestamp(),
          };

          await setDoc(userRef, newUser);

          // Also create the username lookup entry
          const usernameRef = doc(db, 'usernames', fallbackUsername);
          await setDoc(usernameRef, {
            userId: firebaseUser.uid,
            createdAt: serverTimestamp(),
          });

          setUserProfile({ id: firebaseUser.uid, ...newUser } as User);
          // New user needs to set their preferred username
          setNeedsUsername(true);
        }
      } else {
        setUserProfile(null);
        setNeedsUsername(false);
      }

      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Function to update user profile in state (for local updates)
  const updateUserProfile = (updates: Partial<User>) => {
    if (userProfile) {
      setUserProfile({ ...userProfile, ...updates });
      if (updates.username) {
        setNeedsUsername(false);
      }
    }
  };

  // Function to set username via service
  const setUserUsername = async (username: string): Promise<{ success: boolean; error?: string }> => {
    if (!user || !userProfile) {
      return { success: false, error: 'Not authenticated' };
    }

    const result = await setUsernameService(user.uid, username, userProfile.username);

    if (result.success) {
      updateUserProfile({ username: username.toLowerCase() });
    }

    return result;
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(auth, provider);
  };

  const signInWithApple = async () => {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    await signInWithPopup(auth, provider);
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        isLoading,
        needsUsername,
        signInWithGoogle,
        signInWithApple,
        signOut,
        updateUserProfile,
        setUserUsername,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
